import { ReactElement, useState } from 'react';
import { Dialog, DialogTrigger, Popover } from 'react-aria-components';

import { makeStyles } from '@material-ui/core';

import { EmojiPicker } from 'frimousse';

import { Button } from '@backstage/ui';

const useStyles = makeStyles(theme => ({
  popover: {
    backgroundColor: 'var(--bui-bg-surface-1)',
    borderRadius: 'var(--bui-radius-3)',
    border: '1px solid var(--bui-border)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
    outline: 'none',
    overflow: 'hidden',
    '&[data-entering]': {
      animation: '$fadeIn 150ms ease-out',
    },
    '&[data-exiting]': {
      animation: '$fadeOut 100ms ease-in',
    },
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'scale(0.95)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes fadeOut': {
    from: { opacity: 1, transform: 'scale(1)' },
    to: { opacity: 0, transform: 'scale(0.95)' },
  },
  dialog: {
    outline: 'none',
  },
  picker: {
    display: 'flex',
    flexDirection: 'column',
    height: 340,
    width: 320,
  },
  search: {
    margin: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    fontSize: 'var(--bui-font-size-3)',
    backgroundColor: 'var(--bui-bg-surface-2)',
    border: '1px solid var(--bui-border)',
    borderRadius: 'var(--bui-radius-2)',
    color: 'var(--bui-fg-primary)',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--bui-ring)',
    },
    '&::placeholder': {
      color: 'var(--bui-fg-secondary)',
    },
  },
  viewport: {
    flex: 1,
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'var(--bui-scrollbar-thumb) transparent',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--bui-fg-secondary)',
    fontSize: 'var(--bui-font-size-3)',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--bui-fg-secondary)',
    fontSize: 'var(--bui-font-size-3)',
  },
  list: {
    padding: theme.spacing(0, 1),
    // Style emoji buttons
    '& [frimousse-emoji]': {
      all: 'unset',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      fontSize: 'var(--bui-font-size-4)',
      borderRadius: 'var(--bui-radius-2)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'var(--bui-bg-tint-hover)',
      },
      '&:focus': {
        backgroundColor: 'var(--bui-bg-tint)',
        outline: 'none',
      },
    },
    // Style category headers
    '& [frimousse-category-header]': {
      padding: theme.spacing(1.5, 0.5, 0.5),
      fontSize: 'var(--bui-font-size-2)',
      fontWeight: 'var(--bui-font-weight-bold)',
      color: 'var(--bui-fg-secondary)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    // Style emoji rows
    '& [frimousse-row]': {
      display: 'flex',
      gap: 2,
    },
  },
}));

export interface EmojiPickerPopoverProps {
  /** The icon to display in the trigger button */
  icon: ReactElement;
  /** Callback when an emoji is selected */
  onEmojiSelect?: (emoji: string) => void;
}

export const EmojiPickerPopover = ({
  icon,
  onEmojiSelect,
}: EmojiPickerPopoverProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const handleEmojiSelect = (emoji: { emoji: string }) => {
    onEmojiSelect?.(emoji.emoji);
    setIsOpen(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button iconStart={icon} variant="tertiary" size="small" />
      <Popover placement="top" className={classes.popover}>
        <Dialog className={classes.dialog}>
          <EmojiPicker.Root
            className={classes.picker}
            onEmojiSelect={handleEmojiSelect}
          >
            <EmojiPicker.Search
              className={classes.search}
              placeholder="Search emoji..."
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <EmojiPicker.Viewport className={classes.viewport}>
              <EmojiPicker.Loading className={classes.loading}>
                Loading…
              </EmojiPicker.Loading>
              <EmojiPicker.Empty className={classes.empty}>
                No emoji found.
              </EmojiPicker.Empty>
              <EmojiPicker.List className={classes.list} />
            </EmojiPicker.Viewport>
          </EmojiPicker.Root>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
